import React, { useState, useEffect } from 'react'
import type { Question } from '@/types/quiz'

interface QuestionFormModalProps {
  question?: Question | null
  quizId: string
  onSave: (data: Omit<Question, 'id' | 'quizId'>) => Promise<void>
  onClose: () => void
  isSaving: boolean
}

interface FormData {
  questionText: string
  questionType: 'multiple_choice' | 'essay'
  options: string[]
  correctAnswer: number
  points: number
}

interface FormErrors {
  questionText?: string
  options?: string
  correctAnswer?: string
  points?: string
}

export const QuestionFormModal: React.FC<QuestionFormModalProps> = ({
  question,
  // quizId is passed for context but not used directly in this component
  onSave,
  onClose,
  isSaving,
}) => {
  const isEditMode = !!question

  const [formData, setFormData] = useState<FormData>({
    questionText: '',
    questionType: 'multiple_choice',
    options: ['', '', '', ''],
    correctAnswer: 0,
    points: 10,
  })
  const [errors, setErrors] = useState<FormErrors>({})

  useEffect(() => {
    if (question) {
      setFormData({
        questionText: question.questionText,
        questionType: question.questionType,
        options: question.options.length > 0 ? question.options : ['', '', '', ''],
        correctAnswer: question.correctAnswer,
        points: question.points,
      })
    }
  }, [question])

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.questionText.trim()) {
      newErrors.questionText = 'Question text is required'
    }

    if (formData.questionType === 'multiple_choice') {
      const filledOptions = formData.options.filter((opt) => opt.trim())
      if (filledOptions.length < 2) {
        newErrors.options = 'At least 2 options are required'
      }
      if (formData.correctAnswer < 0 || formData.correctAnswer >= filledOptions.length) {
        newErrors.correctAnswer = 'Please select a correct answer'
      }
    }

    if (formData.points < 1) {
      newErrors.points = 'Points must be at least 1'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    // Filter empty options for MC
    const cleanOptions =
      formData.questionType === 'multiple_choice'
        ? formData.options.filter((opt) => opt.trim())
        : []

    await onSave({
      questionText: formData.questionText.trim(),
      questionType: formData.questionType,
      options: cleanOptions,
      correctAnswer: formData.questionType === 'multiple_choice' ? formData.correctAnswer : -1,
      points: formData.points,
      orderIndex: question?.orderIndex ?? 0,
    })
  }

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...formData.options]
    newOptions[index] = value
    setFormData((prev) => ({ ...prev, options: newOptions }))
    setErrors((prev) => ({ ...prev, options: undefined }))
  }

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData((prev) => ({ ...prev, options: [...prev.options, ''] }))
    }
  }

  const removeOption = (index: number) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index)
      setFormData((prev) => ({
        ...prev,
        options: newOptions,
        correctAnswer:
          prev.correctAnswer === index
            ? 0
            : prev.correctAnswer > index
              ? prev.correctAnswer - 1
              : prev.correctAnswer,
      }))
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] border border-slate-100 dark:border-white/5 overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-slate-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined">{isEditMode ? 'edit_note' : 'add_task'}</span>
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900 dark:text-white tracking-tight">
                {isEditMode ? 'Edit Question' : 'Tambah Pertanyaan'}
              </h3>
              <p className="text-xs font-bold text-slate-400 mt-0.5">Atur soal dan jawaban yang benar</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="size-10 rounded-xl flex items-center justify-center hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <form id="question-form" onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Question Type */}
            <div>
              <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1 mb-2">Tipe Soal</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, questionType: 'multiple_choice' }))
                  }
                  className={`p-4 rounded-2xl border text-left transition-all ${
                    formData.questionType === 'multiple_choice'
                      ? 'border-primary bg-primary/5 text-primary shadow-sm shadow-primary/5'
                      : 'border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-white/[0.02] text-slate-500 dark:text-slate-400 hover:border-slate-300'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl mb-2 block">
                    radio_button_checked
                  </span>
                  <p className="font-bold text-sm">Multiple Choice</p>
                  <p className="text-[10px] opacity-70 font-medium">Pilih satu jawaban benar</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, questionType: 'essay' }))}
                  className={`p-4 rounded-2xl border text-left transition-all ${
                    formData.questionType === 'essay'
                      ? 'border-primary bg-primary/5 text-primary shadow-sm shadow-primary/5'
                      : 'border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-white/[0.02] text-slate-500 dark:text-slate-400 hover:border-slate-300'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl mb-2 block">edit_note</span>
                  <p className="font-bold text-sm">Essay</p>
                  <p className="text-[10px] opacity-70 font-medium">Jawaban teks bebas</p>
                </button>
              </div>
            </div>

            {/* Question Text */}
            <div className="space-y-1">
              <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                Pertanyaan <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={formData.questionText}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, questionText: e.target.value }))
                  setErrors((prev) => ({ ...prev, questionText: undefined }))
                }}
                rows={3}
                className={`w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 transition-all outline-none focus:ring-4 focus:ring-primary/10 resize-none ${
                  errors.questionText ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                }`}
                placeholder="Tuliskan pertanyaan di sini..."
              />
              {errors.questionText && (
                <p className="mt-1 text-xs font-bold text-rose-500 ml-1">{errors.questionText}</p>
              )}
            </div>

            {/* Options (for multiple choice) */}
            {formData.questionType === 'multiple_choice' && (
              <div className="space-y-1">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1 mb-2">
                  Pilihan Jawaban <span className="text-rose-500">*</span>
                </label>
                <div className="space-y-3">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, correctAnswer: index }))}
                        className={`flex-shrink-0 size-7 rounded-full border-2 flex items-center justify-center transition-all ${
                          formData.correctAnswer === index
                            ? 'border-primary bg-primary text-white shadow-lg shadow-primary/20'
                            : 'border-slate-300 dark:border-white/20 hover:border-primary/50'
                        }`}
                        title="Set as correct answer"
                      >
                        {formData.correctAnswer === index && (
                          <span className="material-symbols-outlined text-sm">check</span>
                        )}
                      </button>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        className="flex-1 px-5 py-3 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all"
                        placeholder={`Opsi ${index + 1}`}
                      />
                      {formData.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="size-9 rounded-xl flex items-center justify-center hover:bg-rose-500/10 text-slate-400 hover:text-rose-500 transition-all"
                        >
                          <span className="material-symbols-outlined text-[18px]">close</span>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {errors.options && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.options}</p>}
                {errors.correctAnswer && (
                  <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.correctAnswer}</p>
                )}
                {formData.options.length < 6 && (
                  <button
                    type="button"
                    onClick={addOption}
                    className="mt-3 flex items-center gap-2 text-xs font-bold text-primary hover:text-primary/80 transition-colors ml-1"
                  >
                    <span className="material-symbols-outlined text-[16px]">add_circle</span>
                    Tambah Opsi
                  </button>
                )}
                <p className="mt-2 text-[10px] text-slate-400 font-bold ml-1">
                  Klik lingkaran untuk menandai jawaban yang benar
                </p>
              </div>
            )}

            {/* Essay note */}
            {formData.questionType === 'essay' && (
              <div className="p-5 rounded-2xl bg-blue-500/5 border border-blue-500/10">
                <p className="text-xs font-bold text-blue-500">
                  <span className="font-black">Catatan:</span> Pertanyaan essay memerlukan penilaian manual oleh instruktur.
                </p>
              </div>
            )}

            {/* Points */}
            <div className="space-y-1">
              <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                Poin <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                value={formData.points}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, points: Number(e.target.value) }))
                  setErrors((prev) => ({ ...prev, points: undefined }))
                }}
                min="1"
                className={`w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                  errors.points ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                }`}
                placeholder="10"
              />
              {errors.points && <p className="mt-1 text-xs font-bold text-rose-500 ml-1">{errors.points}</p>}
            </div>
          </form>
        </div>

        {/* Footer Actions - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-end gap-3 p-6 border-t border-slate-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] backdrop-blur-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-6 py-3 rounded-2xl text-xs font-black text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-all disabled:opacity-50"
          >
            Batal
          </button>
          <button
            form="question-form"
            type="submit"
            disabled={isSaving}
            className="px-8 py-3 bg-primary text-white rounded-2xl font-black text-xs hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-xl shadow-primary/20"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/30 border-t-white" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">{isEditMode ? 'check_circle' : 'add_circle'}</span>
                <span>{isEditMode ? 'Update' : 'Simpan'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}


